import { Chat, Message } from '../types'
import client from '../api/client'
import toast from 'react-hot-toast'

// ===== Export =====

export interface ExportFormat {
  version: 1
  exportedAt: string
  chats: ExportedChat[]
}

export interface ExportedChat {
  id: string
  title: string
  model: string
  createdAt: string
  updatedAt: string
  messages: {
    id: string
    role: 'user' | 'assistant' | 'system'
    content: string
    timestamp: string
    model?: string
  }[]
}

export function formatChatForExport(chat: Chat): ExportedChat {
  return {
    id: chat.id,
    title: chat.title,
    model: chat.model,
    createdAt: chat.createdAt,
    updatedAt: chat.updatedAt,
    messages: chat.messages.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      timestamp: m.timestamp,
      model: m.model,
    })),
  }
}

export function exportChats(chats: Chat[]) {
  const data: ExportFormat = {
    version: 1,
    exportedAt: new Date().toISOString(),
    chats: chats.map(formatChatForExport),
  }

  const json = JSON.stringify(data, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const label = chats.length === 1 ? chats[0].title.replace(/[^a-zA-Z0-9а-яА-ЯёЁ _-]/g, '').slice(0, 50) : `${chats.length}-chats`
  a.download = `neuralspace-chat-${label}-${new Date().toISOString().slice(0, 10)}.json`
  a.click()
  URL.revokeObjectURL(url)
  toast.success(`Экспортировано ${chats.length} диалог(ов)`)
}

// ===== Import =====

export async function importChatsFromFile(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string
        const data: ExportFormat = JSON.parse(text)

        if (!data.version || !data.chats || !Array.isArray(data.chats)) {
          throw new Error('Неверный формат файла')
        }

        let imported = 0
        for (const chat of data.chats) {
          try {
            // Создаём новый чат на сервере
            const { data: newChat } = await client.post('/chat', {
              model: chat.model || 'gpt-5.4-nano',
              title: chat.title || 'Импортированный чат',
            })

            // Импортируем все сообщения разом через специальный эндпоинт
            if (chat.messages && chat.messages.length > 0) {
              await client.post(`/chat/${newChat.id}/messages/import`, {
                messages: chat.messages.map((m) => ({
                  role: m.role,
                  content: m.content,
                  timestamp: m.timestamp,
                  model: m.model,
                })),
              })
            }

            imported++
          } catch (err) {
            console.error('Failed to import chat:', chat.title, err)
          }
        }

        resolve(imported)
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = () => reject(new Error('Ошибка чтения файла'))
    reader.readAsText(file)
  })
}

// ===== Share =====

export function getShareUrl(chatId: string): string {
  const base = window.location.origin
  return `${base}/chat/${chatId}`
}

export function copyShareLink(chatId: string) {
  const url = getShareUrl(chatId)
  navigator.clipboard.writeText(url)
  toast.success('Ссылка скопирована в буфер обмена')
}

export function shareToSocial(platform: 'telegram' | 'whatsapp' | 'vkontakte', chatId: string) {
  const url = encodeURIComponent(getShareUrl(chatId))
  let shareUrl = ''

  switch (platform) {
    case 'telegram':
      shareUrl = `https://t.me/share/url?url=${url}`
      break
    case 'whatsapp':
      shareUrl = `https://wa.me/?text=${url}`
      break
    case 'vkontakte':
      shareUrl = `https://vk.com/share.php?url=${url}`
      break
  }

  if (shareUrl) {
    window.open(shareUrl, '_blank', 'noopener,noreferrer')
  }
}
